import React, { useCallback, useLayoutEffect } from 'react';
import { theme } from '@garnish/constants';

import { useIsLoggedIn } from '@order/AuthMachine';
import {
  LoadingAnimation,
  NotFoundView,
  RefreshErrorState,
  SweetpassFreeTrialNotEligible,
  SweetpassSignedOut,
} from '@order/components';
import { TierName } from '@order/graphql';
import { useSweetpassPlan, useSweetpassTier } from '@order/hooks';

import type { SweetpassUpgradeScreenProps } from '../../navigation/AppNavigation.props';
import { useSweetpassOptIn } from './hooks';
import { SweetpassUpgradeScreenContent } from './SweetpassUpgradeScreenContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassUpgradeScreen = (props: SweetpassUpgradeScreenProps) => {
  const { navigation, route } = props;

  // ─── Params ──────────────────────────────────────────────────────────

  const {
    redirectToAfterUpgrade,
    redirectToOrderId,
    planId: planIdFromParams,
    campaignId,
  } = route.params ?? {};

  // ─── Queries ─────────────────────────────────────────────────────────

  const {
    isSweetpassTierLoading,
    sweetpassNextTierId,
    sweetpassNextTierName,
    isEligibleForFreeTrial,
    trialDuration,
    trialDurationSingular,
    hasUnknownTierError,
    refetchSweetpassTier,
  } = useSweetpassTier('cache-and-network');

  const {
    planId,
    planPrice,
    isLoadingPlan,
    hasFreeTrial,
    billingFrequencyUnit,
    hasUnknownPlanError,
  } = useSweetpassPlan(planIdFromParams);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const { isConfirmingOptIn, optInSweetpass } = useSweetpassOptIn({
    planId,
    campaignId,
    redirectToAfterUpgrade,
    redirectToOrderId,
    refetchSweetpassTier,
  });

  // Redirects logged-out customers to Login -> Sweetpass Upgrade screen.
  const handlePressSignIn = useCallback(() => {
    navigation.replace('Auth', {
      screen: 'Login',
      params: {
        redirect: 'sweetpassUpgrade',
        redirectToPlanId: planIdFromParams,
        redirectToCampaignId: campaignId,
      },
    });
  }, [navigation, planIdFromParams, campaignId]);

  const handlePressJoinNow = useCallback(() => {
    navigation.replace('Auth', {
      screen: 'Join',
      params: {
        redirect: 'sweetpassUpgrade',
        redirectToPlanId: planIdFromParams,
        redirectToCampaignId: campaignId,
      },
    });
  }, [navigation, planIdFromParams, campaignId]);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const isNextTierSweetpassPlus =
    sweetpassNextTierName === TierName.SweetpassPlus;
  const isLoading = isLoadingPlan || isSweetpassTierLoading;
  const isMissingPlan = isNextTierSweetpassPlus && !planId;
  const isMissingRequiredData = isMissingPlan || !sweetpassNextTierId;
  const isNotEligible = hasFreeTrial && !isEligibleForFreeTrial;
  const hasUnknownError = hasUnknownTierError || hasUnknownPlanError;

  // ─── Effects ─────────────────────────────────────────────────────────

  // Reset header background color
  useLayoutEffect(() => {
    navigation.setOptions({
      headerStyle: {
        backgroundColor: theme.colors.OATMEAL,
      },
    });
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return (
      <SweetpassSignedOut
        testID="sweetpass-signed-out"
        onPressSignIn={handlePressSignIn}
        onPressJoinNow={handlePressJoinNow}
      />
    );
  }

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (hasUnknownError) {
    return <RefreshErrorState />;
  }

  if (isMissingRequiredData) {
    return <NotFoundView />;
  }

  if (isNotEligible) {
    return <SweetpassFreeTrialNotEligible />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SweetpassUpgradeScreenContent
      campaignId={campaignId}
      sweetpassNextTierName={sweetpassNextTierName}
      isNextTierSweetpassPlus={isNextTierSweetpassPlus}
      isEligibleForFreeTrial={isEligibleForFreeTrial}
      hasFreeTrial={hasFreeTrial}
      planPrice={planPrice}
      trialDuration={trialDuration}
      trialDurationSingular={trialDurationSingular}
      billingFrequencyUnit={billingFrequencyUnit}
      isConfirmingOptIn={isConfirmingOptIn}
      onCtaConfirmation={optInSweetpass}
    />
  );
};
